import { create } from 'zustand'
import type { ThemePreference, UserSettingsDTO } from '@shared/types'
import { api } from '@/lib/api'

/** Same key the pre-paint script in index.html reads. Changing it flashes on reload. */
const STORAGE_KEY = 'argus.theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

type Resolved = 'light' | 'dark'

interface ThemeState {
  theme: ThemePreference
  resolved: Resolved
  setTheme: (t: ThemePreference) => void
}

function readStored(): ThemePreference | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : null
  } catch {
    // Private mode can throw on access, not just on write.
    return null
  }
}

function prefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches
}

function resolveTheme(theme: ThemePreference): Resolved {
  if (theme !== 'system') return theme
  return prefersDark() ? 'dark' : 'light'
}

function applyClass(resolved: Resolved): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

const initialTheme = readStored() ?? 'system'

export const useThemeStore = create<ThemeState>((set) => ({
  theme: initialTheme,
  resolved: resolveTheme(initialTheme),

  setTheme: (theme) => {
    const resolved = resolveTheme(theme)
    applyClass(resolved)
    set({ theme, resolved })
    try {
      localStorage.setItem(STORAGE_KEY, theme)
    } catch {
      // Without storage the choice lasts the session; the toggle still works.
    }
    // Mirror it onto the account so the preference travels, but a toggle must
    // never wait on — or be undone by — the network.
    void api.patch<UserSettingsDTO>('/settings', { theme }).catch(() => undefined)
  }
}))

/**
 * Applies the theme stored on the account. Used once per load by the settings
 * store, and deliberately yields to an explicit choice made on this device.
 *
 * Nothing is written to localStorage: leaving this device "unset" is what lets it
 * keep following the account.
 */
export function adoptRemoteTheme(theme: ThemePreference): void {
  if (readStored() !== null) return
  const resolved = resolveTheme(theme)
  applyClass(resolved)
  useThemeStore.setState({ theme, resolved })
}

// The inline script in index.html already set the class; re-applying keeps the
// two in step if the stored value was unreadable or invalid there.
applyClass(resolveTheme(initialTheme))

if (typeof matchMedia === 'function') {
  matchMedia(DARK_QUERY).addEventListener('change', (event) => {
    if (useThemeStore.getState().theme !== 'system') return
    const resolved: Resolved = event.matches ? 'dark' : 'light'
    applyClass(resolved)
    useThemeStore.setState({ resolved })
  })
}
